/**
 * React Query v4 Configuration for Global Hooks
 * Centralized configuration for caching, retries, and stale time
 * Used by hooks in src/hooks/queries/
 */

import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 2 minutes before considering it stale
      staleTime: 2 * 60 * 1000,
      // Keep unused data in cache for 10 minutes (gcTime replaces cacheTime in v4+)
      gcTime: 10 * 60 * 1000,
      // Retry failed requests with smart logic
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        return failureCount < 2;
      },
      // Disable refetch on window focus (reduces unnecessary API calls)
      refetchOnWindowFocus: false,
      // Don't refetch on reconnect by default
      refetchOnReconnect: false,
      // Only refetch on mount if data is stale
      refetchOnMount: 'stale',
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
    },
  },
});

/**
 * Helper function to serialize array/object values in query keys
 * Prevents query key changes when arrays have same values but different references
 */
const serializeValue = (value) => {
  if (Array.isArray(value)) {
    return value.length > 0 ? JSON.stringify([...value].sort()) : '';
  }
  if (typeof value === 'object' && value !== null) {
    return JSON.stringify(value);
  }
  return value;
};

/**
 * Global Query Keys Factory for Hooks
 * Centralized query key management for global hooks
 * Used by hooks in src/hooks/queries/
 */
export const queryKeys = {
  // Practice areas
  practiceAreas: {
    all: ['practice-areas'],
    list: (filters) => {
      // Support both old format (status string) and new format (filters object)
      if (typeof filters === 'string') {
        return ['practice-areas', 'list', filters];
      }
      // New format with all parameters
      return [
        'practice-areas',
        'list',
        filters?.status || 'active',
        filters?.sort_order || 'alphabet_ascending',
        filters?.pagination_token || '',
        filters?.pagination_direction || 'forward',
        filters?.pageSize || 10,
        filters?.loadFullList !== undefined ? filters.loadFullList : true,
      ];
    },
    // Paginated query key for react-select-async-paginate
    paginated: (filters) => [
      'practice-areas',
      'paginated',
      filters?.searchQuery || '',
      filters?.pagination_token || '',
      filters?.pageSize || 20,
      filters?.status || 'active',
      filters?.sort_order || 'alphabet_ascending',
    ],
    // Per-page dropdown cache key for PracticeAreaSelectV2 (getGlobalDropdown-based)
    dropdown: (caseId, isLeadSpecific, search, page, pageSize) => [
      'practice-areas',
      'dropdown',
      caseId ?? '',
      isLeadSpecific,
      search,
      page,
      pageSize,
    ],
  },
  // Event types
  eventTypes: {
    all: ['event-types'],
    list: (practiceAreaId) => ['event-types', 'list', practiceAreaId || ''],
    // Paginated query key for react-select-async-paginate
    paginated: (filters) => [
      'event-types',
      'paginated',
      filters?.searchQuery || '',
      filters?.pagination_token || '',
      filters?.pageSize || 20,
      filters?.sort_order || 'alphabet_ascending',
      filters?.practiceAreaId || '',
    ],
  },
  // Locations
  locations: {
    all: ['locations'],
    list: (filters) => ['locations', 'list', filters || {}],
    // Paginated query key for react-select-async-paginate
    paginated: (filters) => [
      'locations',
      'paginated',
      filters?.searchQuery || '',
      filters?.pagination_token || '',
      filters?.pageSize || 20,
      filters?.contactStatus || 'ACTIVE',
      filters?.is_court_location !== undefined ? filters.is_court_location : true,
      filters?.is_event_location !== undefined ? filters.is_event_location : true,
    ],
  },
  // Conference Rooms
  conferenceRooms: {
    all: ['conference-rooms'],
    list: (officeLocationId) => ['conference-rooms', 'list', officeLocationId || ''],
    // Paginated query key for react-select-async-paginate
    paginated: (filters) => [
      'conference-rooms',
      'paginated',
      filters?.officeLocationId || '',
      filters?.searchQuery || '',
      filters?.page || 0,
      filters?.pageSize || 20,
    ],
  },
  // Google Calendars
  googleCalendars: {
    all: ['google-calendars'],
    list: () => ['google-calendars', 'list'],
  },
  // Calendar Events
  calendarEvents: {
    all: ['calendar', 'events'],
    list: (view, start, end, filters, selectedUsers, timezone, caseId) => [
      'calendar',
      'events',
      view,
      start,
      end,
      filters,
      selectedUsers,
      timezone,
      caseId,
    ],
    detail: (eventId) => ['calendar', 'event', eventId],
    dayEvents: (date, filters, selectedUsers, timezone, caseId, contactId) => [
      'calendar',
      'events',
      date,
      filters,
      selectedUsers,
      timezone,
      caseId,
      contactId,
    ],
  },
  // Expense types
  expenseTypes: {
    all: ['expense-types'],
    list: (pageLimit, pagination_token, pagination_direction, next, filter) => [
      'expense-types',
      'list',
      pageLimit,
      pagination_token,
      pagination_direction,
      next,
      filter,
    ],
  },
  // Users
  users: {
    all: ['users'],
    list: (
      status,
      pageLimit,
      pagination_token,
      pagination_direction,
      keyword,
      next,
      is_include_contacts,
      is_detailed,
      is_confirmed_user,
      is_include_groups
    ) => [
      'users',
      'list',
      status,
      pageLimit,
      pagination_token,
      pagination_direction,
      keyword,
      next,
      is_include_contacts,
      is_detailed,
      is_confirmed_user,
      is_include_groups,
    ],
  },
  // Firm Users RDS (page-based, for report filters)
  firmUsersRds: {
    all: ['firm-users-rds'],
    list: (status, limit, page, is_confirmed_user, team_member) => [
      'firm-users-rds',
      'list',
      status,
      limit,
      page,
      is_confirmed_user,
      team_member,
    ],
  },
  // Firm Users (filtered and sorted)
  firmUsers: {
    all: ['firm-users'],
    list: (
      status,
      pageLimit,
      pagination_token,
      pagination_direction,
      keyWord,
      next,
      is_include_contacts,
      is_detailed,
      is_confirmed_user,
      is_include_groups,
      active_groups,
      team_member = false
    ) => [
      'firm-users',
      'list',
      status,
      pageLimit,
      pagination_token,
      pagination_direction,
      keyWord,
      next,
      is_include_contacts,
      is_detailed,
      is_confirmed_user,
      is_include_groups,
      active_groups,
      team_member,
    ],
  },
  // Activity types
  activityTypes: {
    all: ['activity-types'],
    list: (pageLimit, pagination_token, pagination_direction, next, filters) => [
      'activity-types',
      'list',
      pageLimit,
      pagination_token,
      pagination_direction,
      next,
      filters,
    ],
  },
  // Accounts
  accounts: {
    all: ['accounts'],
    list: (pageLimit, pagination_token, pagination_direction, next, payloadData) => [
      'accounts',
      'list',
      pageLimit,
      pagination_token,
      pagination_direction,
      next,
      payloadData,
    ],
  },
  // Confido Bank Accounts
  confidoBankAccounts: {
    all: ['confido-bank-accounts'],
    list: (accountType = '') => ['confido-bank-accounts', 'list', accountType],
  },
  // Payment Structure
  paymentStructure: {
    all: ['payment-structure'],
    list: (limit, next) => ['payment-structure', 'list', limit, next ?? ''],
  },
  // Tax
  tax: {
    all: ['tax'],
    list: () => ['tax', 'list'],
  },
  // Case Stages
  caseStages: {
    all: ['case-stages'],
    list: (practice_area, pageLimit, pagination_token, pagination_direction) => [
      'case-stages',
      'list',
      serializeValue(practice_area),
      pageLimit,
      pagination_token,
      pagination_direction,
    ],
    // Per-page dropdown cache key for CaseStageSelectV2 (list-case-stages-v2)
    dropdownV2: (practiceAreaId, search, page, pageSize) => [
      'case-stages',
      'dropdown-v2',
      serializeValue(practiceAreaId),
      search,
      page,
      pageSize,
    ],
  },
  // Case Matters
  caseMatters: {
    all: ['case-matters'],
    /** Full matter list via v2 (eager pagination) — `useCaseMattersQuery` / `useCaseMatter`; report UIs use CaseMatterSelect async only. */
    fullListV2: (practiceAreaId, is_pi_case, loadFullList = true, pageLimit) => [
      'case-matters',
      'full-list-v2',
      serializeValue(practiceAreaId),
      Boolean(is_pi_case),
      loadFullList !== false,
      pageLimit ?? null,
    ],
    /** Settings → Custom descriptors → Case matter table (`/list-case-matters-v2`, page + limit) */
    settingsListV2: (page, limit, sort_by, sort_order) => ['case-matters', 'settings-list-v2', page, limit, sort_by, sort_order],
    /** Async select / CaseMatterSelect — `list-case-matters-v2` (page + limit) */
    paginated: (filters) => [
      'case-matters',
      'paginated-v2',
      filters?.searchQuery || '',
      filters?.page || 1,
      filters?.pageSize || 20,
      serializeValue(filters?.practiceAreaId || ''),
      Boolean(filters?.is_pi_case),
    ],
  },
  // Case Tags
  caseTags: {
    all: ['case-tags'],
    list: (isLead, isPiCase) => ['case-tags', 'list', isLead, isPiCase],
  },
  // Venues
  venues: {
    all: ['venues'],
    list: (page_limit, pagination_token, pagination_direction, sort_order, practiceAreaId) => [
      'venues',
      'list',
      page_limit,
      pagination_token,
      pagination_direction,
      sort_order,
      serializeValue(practiceAreaId),
    ],
  },
  // Case Outcomes
  caseOutcomes: {
    all: ['case-outcomes'],
    list: (isLead, sort_order, limit, token, direction, practice_area_id) => [
      'case-outcomes',
      'list',
      isLead,
      sort_order,
      limit,
      token,
      direction,
      practice_area_id,
    ],
  },
  // Case Close Reasons
  caseCloseReasons: {
    all: ['case-close-reasons'],
    list: (isLead, sort_order, limit, token, direction, practice_area_id) => [
      'case-close-reasons',
      'list',
      isLead,
      sort_order,
      limit,
      token,
      direction,
      practice_area_id,
    ],
  },
  /** Settings custom descriptors: `list-closure-reasons-v2` (page + limit), BillingV2-style keys */
  closureReasonsV2: {
    all: ['closure-reasons-v2'],
    listV2: (filters, page, pageSize) => ['closure-reasons-v2', 'list', 'v2', { filters, page, pageSize }],
    dropdown: (isLead, sortBy, sortOrder, keyword, page, pageSize, practiceAreaId = '') => [
      'closure-reasons-v2',
      'dropdown',
      isLead,
      sortBy,
      sortOrder,
      keyword ?? '',
      page,
      pageSize,
      practiceAreaId ?? '',
    ],
  },
  /** Settings custom descriptors: `list-case-outcomes-v2` (page + limit) */
  caseOutcomesV2: {
    all: ['case-outcomes-v2'],
    listV2: (filters, page, pageSize) => ['case-outcomes-v2', 'list', 'v2', { filters, page, pageSize }],
    dropdown: (isLead, sortBy, sortOrder, keyword, page, pageSize, practiceAreaId = '') => [
      'case-outcomes-v2',
      'dropdown',
      isLead,
      sortBy,
      sortOrder,
      keyword ?? '',
      page,
      pageSize,
      practiceAreaId ?? '',
    ],
  },
  /** Settings custom descriptors: `list-lead-stages-v2` (page + limit) */
  leadStagesV2: {
    all: ['lead-stages-v2'],
    listV2: (filters, page, pageSize) => ['lead-stages-v2', 'list', 'v2', { filters, page, pageSize }],
    dropdown: (search, page, pageSize, leadStageStatus = 'ACTIVE', sortBy = 'lead_stage_name', sortOrder = 'asc', practiceArea = null) => [
      'lead-stages-v2',
      'dropdown',
      search ?? '',
      page,
      pageSize,
      leadStageStatus ?? '',
      sortBy ?? '',
      sortOrder ?? '',
      practiceArea ?? null,
    ],
  },
  /** Settings custom descriptors: `list-job-title-rds` (page + limit) */
  jobTitlesRds: {
    all: ['job-titles-rds'],
    /** Flat key — page and limit are direct array elements so React Query detects page changes reliably. */
    list: (page, limit, sort_order, practice_area_id, keyword) => [
      'job-titles-rds',
      'list',
      page,
      limit,
      sort_order ?? 'asc',
      practice_area_id ?? '',
      keyword ?? '',
    ],
    dropdown: (sortOrder, keyword, page, pageSize, practiceAreaId = '') => [
      'job-titles-rds',
      'dropdown',
      sortOrder ?? 'asc',
      keyword ?? '',
      page,
      pageSize,
      practiceAreaId ?? '',
    ],
  },
  // Announcements
  announcements: {
    all: ['announcements'],
    list: (pageLimit) => ['announcements', 'list', pageLimit],
  },
  // Office Locations
  officeLocations: {
    all: ['officeLocations'],
    dropdown: (filters) => ['officeLocations', 'dropdown', filters?.searchQuery || '', filters?.page || 1, filters?.pageSize || 20],
  },
  // Timer List (used by TimerManager in Layout)
  timerList: {
    all: ['timer-list'],
    list: (userId) => ['timer-list', 'list', userId || ''],
  },
  // Case and Lead List for Timer (used by TimerOverlay)
  timerCaseLeadList: {
    all: ['timer-case-lead-list'],
    list: (keyword) => ['timer-case-lead-list', 'list', keyword || ''],
  },
  // File Manager V2
  fileManager: {
    all: ['file-manager'],
    lists: () => ['file-manager', 'list'],
    list: (params) => ['file-manager', 'list', serializeValue(params)],
    tags: (caseId) => ['file-manager', 'tags', caseId || ''],
    clients: (caseId) => ['file-manager', 'clients', caseId || ''],
    folders: (caseId, data) => ['file-manager', 'folders', caseId || '', serializeValue(data)],
    templates: (caseId) => ['file-manager', 'templates', caseId || ''],
  },
};

/**
 * Invalidate all case-matter queries (settings list v2, full-list v2, async-paginated selects, report filters).
 * Use after create / update / delete case matter from any screen.
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 */
export const invalidateCaseMatterQueries = (queryClient) => queryClient.invalidateQueries({ queryKey: queryKeys.caseMatters.all });
